import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Grid, MenuItem, Paper, TextField, Typography } from '@mui/material';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { BillOfLadingService } from '../../services/billOfLading.service';
import { BillOfLadingType, CreateBillOfLadingRequest, FreightTerms } from '../../interfaces/commercial.interface';
import CommercialPartyAutocomplete from './CommercialPartyAutocomplete';
import RefAutocomplete, { RefOption } from './common/RefAutocomplete';
import { fetchAllPages } from './common/fetchAllPages';
import { ExpFormService } from '../../services/expForm.service';
import { ExportLcService } from '../../services/exportLc.service';
import { BuyerOrderService } from '../../services/buyerOrder.service';
import { enumLabel } from '../../utils/garmentsLabels';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';

const today = () => toLocalIsoDate();

const BL_TYPES: BillOfLadingType[] = ['ON_BOARD', 'RECEIVED_FOR_SHIPMENT', 'SEAWAY', 'HOUSE', 'MASTER'];
const FREIGHT_TERMS: FreightTerms[] = ['PREPAID', 'COLLECT'];

const EMPTY = {
    blNumber: '', blDate: today(), blType: '' as BillOfLadingType | '',
    expFormId: '', exportLcId: '', buyerOrderId: '',
    forwarderName: '', carrierName: '', vesselName: '', voyageNumber: '',
    containerNumber: '', sealNumber: '', portOfLoading: '', portOfDischarge: '', placeOfDelivery: '',
    shippingMarks: '', freightTerms: '' as FreightTerms | '', remarks: '',
};

/** blType/freightTerms are descriptive enum selects (backend BillOfLadingType/FreightTerms),
 * nullable — no default guard reads them. */
const BillOfLadingForm: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { id } = useParams<{ id: string }>();
    const isEdit = !!id;
    const institutionId = AuthService.getSelectedInstitution()!;

    const [form, setForm] = useState({ ...EMPTY });
    const [forwarderPartyId, setForwarderPartyId] = useState<string | null>(null);
    const [carrierPartyId, setCarrierPartyId] = useState<string | null>(null);
    const [expFormOptions, setExpFormOptions] = useState<RefOption[]>([]);
    const [exportLcOptions, setExportLcOptions] = useState<RefOption[]>([]);
    const [buyerOrderOptions, setBuyerOrderOptions] = useState<RefOption[]>([]);
    const [refOptionsLoading, setRefOptionsLoading] = useState(false);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setRefOptionsLoading(true);
            try {
                const [expForms, exportLcs, buyerOrders] = await Promise.all([
                    fetchAllPages((page, size) => ExpFormService.list(institutionId, { page, size })),
                    fetchAllPages((page, size) => ExportLcService.list(institutionId, { page, size })),
                    fetchAllPages((page, size) => BuyerOrderService.list(institutionId, page, size)),
                ]);
                if (cancelled) return;
                setExpFormOptions(expForms.map(f => ({
                    id: f.id, label: f.buyerName ? `${f.expNumber} — ${f.buyerName}` : f.expNumber,
                })));
                setExportLcOptions(exportLcs.map(lc => ({
                    id: lc.id, label: lc.buyerName ? `${lc.lcNumber} — ${lc.buyerName}` : lc.lcNumber,
                })));
                setBuyerOrderOptions(buyerOrders.map(o => ({
                    id: o.id, label: o.orderDate ? `${o.orderNumber} — ${formatDate(o.orderDate)}` : o.orderNumber,
                })));
            } catch (e) {
                if (!cancelled) toast.showApiError(e, 'Failed to load EXP form / export LC / buyer order options');
            } finally {
                if (!cancelled) setRefOptionsLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    useEffect(() => {
        if (!id) return;
        (async () => {
            try {
                setLoading(true);
                const bl = await BillOfLadingService.getById(institutionId, id);
                setForm({
                    blNumber: bl.blNumber, blDate: bl.blDate, blType: bl.blType ?? '',
                    expFormId: bl.expFormId ?? '', exportLcId: bl.exportLcId ?? '', buyerOrderId: bl.buyerOrderId ?? '',
                    forwarderName: bl.forwarderName ?? '', carrierName: bl.carrierName ?? '',
                    vesselName: bl.vesselName ?? '', voyageNumber: bl.voyageNumber ?? '',
                    containerNumber: bl.containerNumber ?? '', sealNumber: bl.sealNumber ?? '',
                    portOfLoading: bl.portOfLoading ?? '', portOfDischarge: bl.portOfDischarge ?? '',
                    placeOfDelivery: bl.placeOfDelivery ?? '', shippingMarks: bl.shippingMarks ?? '',
                    freightTerms: bl.freightTerms ?? '', remarks: bl.remarks ?? '',
                });
                // Fix round 1: the picker pre-selects from the bare id -- CommercialPartyAutocomplete
                // resolves it against its loaded options or fetches it individually if needed.
                setForwarderPartyId(bl.forwarderPartyId ?? null);
                setCarrierPartyId(bl.carrierPartyId ?? null);
            } catch (e) {
                toast.showApiError(e, 'Failed to load bill of lading');
            } finally {
                setLoading(false);
            }
        })();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const set = (k: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setForm(f => ({ ...f, [k]: e.target.value }));

    const validate = (): string | null => {
        if (!form.blNumber.trim()) return 'B/L number is required.';
        if (!form.blDate) return 'B/L date is required.';
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }
        setError(null);

        const dto: CreateBillOfLadingRequest = {
            blNumber: form.blNumber.trim(),
            blDate: form.blDate,
            blType: form.blType || undefined,
            expFormId: form.expFormId.trim() || undefined,
            exportLcId: form.exportLcId.trim() || undefined,
            buyerOrderId: form.buyerOrderId.trim() || undefined,
            forwarderName: form.forwarderName.trim() || undefined,
            carrierName: form.carrierName.trim() || undefined,
            forwarderPartyId: forwarderPartyId ?? undefined,
            carrierPartyId: carrierPartyId ?? undefined,
            vesselName: form.vesselName.trim() || undefined,
            voyageNumber: form.voyageNumber.trim() || undefined,
            containerNumber: form.containerNumber.trim() || undefined,
            sealNumber: form.sealNumber.trim() || undefined,
            portOfLoading: form.portOfLoading.trim() || undefined,
            portOfDischarge: form.portOfDischarge.trim() || undefined,
            placeOfDelivery: form.placeOfDelivery.trim() || undefined,
            shippingMarks: form.shippingMarks.trim() || undefined,
            freightTerms: form.freightTerms || undefined,
            remarks: form.remarks.trim() || undefined,
        };

        try {
            setSaving(true);
            const saved = isEdit
                ? await BillOfLadingService.update(institutionId, id!, dto)
                : await BillOfLadingService.create(institutionId, dto);
            toast.showSuccess(isEdit ? 'Bill of lading updated' : 'Bill of lading created');
            navigate(`/commercial/bills-of-lading/${saved.id}`);
        } catch (err) {
            toast.showApiError(err, isEdit ? 'Failed to update bill of lading' : 'Failed to create bill of lading');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ p: 3, maxWidth: 900 }} data-help-page="commercial.blForm.page">
            <Typography variant="h5" sx={{ mb: 2 }}>{isEdit ? 'Edit Bill of Lading' : 'New Bill of Lading'}</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Paper sx={{ p: 3 }} data-help-section="commercial.blForm.section.details">
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField data-help="commercial.blForm.field.blNumber" label="B/L Number" fullWidth required value={form.blNumber} onChange={set('blNumber')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField data-help="commercial.blForm.field.blDate" label="B/L Date" type="date" fullWidth required InputLabelProps={{ shrink: true }}
                                value={form.blDate} onChange={set('blDate')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField select data-help="commercial.blForm.field.blType" label="B/L Type" fullWidth value={form.blType} onChange={set('blType')}>
                                <MenuItem value="">—</MenuItem>
                                {BL_TYPES.map(t => <MenuItem key={t} value={t}>{enumLabel(t)}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField select data-help="commercial.blForm.field.freightTerms" label="Freight Terms" fullWidth value={form.freightTerms} onChange={set('freightTerms')}>
                                <MenuItem value="">—</MenuItem>
                                {FREIGHT_TERMS.map(t => <MenuItem key={t} value={t}>{enumLabel(t)}</MenuItem>)}
                            </TextField>
                        </Grid>

                        <Grid size={12}><Typography variant="subtitle2">Linked documents</Typography></Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <RefAutocomplete
                                data-help="commercial.blForm.field.expForm"
                                label="EXP Form"
                                options={expFormOptions}
                                loading={refOptionsLoading}
                                value={form.expFormId || null}
                                onChange={(newId) => setForm(f => ({ ...f, expFormId: newId ?? '' }))}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <RefAutocomplete
                                data-help="commercial.blForm.field.exportLc"
                                label="Export LC"
                                options={exportLcOptions}
                                loading={refOptionsLoading}
                                value={form.exportLcId || null}
                                onChange={(newId) => setForm(f => ({ ...f, exportLcId: newId ?? '' }))}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <RefAutocomplete
                                data-help="commercial.blForm.field.buyerOrder"
                                label="Buyer Order"
                                options={buyerOrderOptions}
                                loading={refOptionsLoading}
                                value={form.buyerOrderId || null}
                                onChange={(newId) => setForm(f => ({ ...f, buyerOrderId: newId ?? '' }))}
                            />
                        </Grid>

                        <Grid size={12}><Typography variant="subtitle2">Forwarder / carrier</Typography></Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField data-help="commercial.blForm.field.forwarderName" label="Forwarder Name" fullWidth value={form.forwarderName} onChange={set('forwarderName')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField data-help="commercial.blForm.field.carrierName" label="Carrier Name" fullWidth value={form.carrierName} onChange={set('carrierName')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            {/* Picking a forwarder sets the id AND fills the text field above; the
                                text stays editable afterwards. Clearing the picker clears only the
                                id -- the typed name is left untouched. */}
                            <CommercialPartyAutocomplete
                                institutionId={institutionId}
                                partyTypes={['FREIGHT_FORWARDER']}
                                data-help="commercial.blForm.field.forwarderParty"
                                label="Forwarder (party lookup)"
                                value={forwarderPartyId}
                                onChange={(party) => {
                                    setForwarderPartyId(party?.id ?? null);
                                    if (party) setForm(f => ({ ...f, forwarderName: party.name }));
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <CommercialPartyAutocomplete
                                institutionId={institutionId}
                                partyTypes={['SHIPPING_LINE', 'FREIGHT_FORWARDER']}
                                data-help="commercial.blForm.field.carrierParty"
                                label="Carrier (party lookup)"
                                value={carrierPartyId}
                                onChange={(party) => {
                                    setCarrierPartyId(party?.id ?? null);
                                    if (party) setForm(f => ({ ...f, carrierName: party.name }));
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField data-help="commercial.blForm.field.vesselName" label="Vessel Name" fullWidth value={form.vesselName} onChange={set('vesselName')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField data-help="commercial.blForm.field.voyageNumber" label="Voyage Number" fullWidth value={form.voyageNumber} onChange={set('voyageNumber')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField data-help="commercial.blForm.field.containerNumber" label="Container Number" fullWidth value={form.containerNumber} onChange={set('containerNumber')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField data-help="commercial.blForm.field.sealNumber" label="Seal Number" fullWidth value={form.sealNumber} onChange={set('sealNumber')} />
                        </Grid>

                        <Grid size={12}><Typography variant="subtitle2">Ports</Typography></Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField data-help="commercial.blForm.field.portOfLoading" label="Port of Loading" fullWidth value={form.portOfLoading} onChange={set('portOfLoading')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField data-help="commercial.blForm.field.portOfDischarge" label="Port of Discharge" fullWidth value={form.portOfDischarge} onChange={set('portOfDischarge')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField data-help="commercial.blForm.field.placeOfDelivery" label="Place of Delivery" fullWidth value={form.placeOfDelivery} onChange={set('placeOfDelivery')} />
                        </Grid>

                        <Grid size={12}>
                            <TextField data-help="commercial.blForm.field.shippingMarks" label="Shipping Marks" fullWidth multiline rows={2} value={form.shippingMarks} onChange={set('shippingMarks')} />
                        </Grid>
                        <Grid size={12}>
                            <TextField data-help="commercial.blForm.field.remarks" label="Remarks" fullWidth multiline rows={3} value={form.remarks} onChange={set('remarks')} />
                        </Grid>

                        <Grid size={12} sx={{ display: 'flex', gap: 1 }}>
                            <Button data-help="commercial.blForm.save" type="submit" variant="contained" disabled={saving}>
                                {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Bill of Lading'}
                            </Button>
                            <Button data-help="commercial.blForm.cancel" variant="outlined" onClick={() => navigate(-1)}>Cancel</Button>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
        </Box>
    );
};

export default BillOfLadingForm;
